import { FC, ReactNode, useCallback, useMemo } from 'react';
import { useClassifierGroupsQuery } from '../../classifier/query/useClassifierGroupsQuery';
import { useGetIdFromName } from '../../common/form';
import { extractRowIndexAndColumnName } from '../../common/form/table/tanstack/extractRowIndexAndColumnName';
import { formatCodeName, getDatePeriodArray } from '../../common/utils';
import { Controller, useFormContext } from 'react-hook-form';
import { Flex, Spin, TreeSelect, TreeSelectProps } from 'antd';
import { DataObjectValidFormDataType } from '../schema';
import { CategoryGroupType, ClassifierGroupType } from '../../classifier';
import { filterCategoryPeriod, filterClassifierPeriod } from '../utils';
import { DimensionClassifierValue } from './DimensionClassifierValue';
import { getCategoryLabel } from './utils';

interface ClassifierOptionType {
  value: string | number;
  title: string;
  displayLabel: ReactNode;
  classifier: ClassifierGroupType;
  allCategories?: boolean;
  levelNumber?: number;
  category?: CategoryGroupType;
  children?: ClassifierOptionType[];
}

export interface ClassifierGroupSelectProps
  extends Omit<TreeSelectProps, 'id'> {
  name: `${string}.${number}.${string}`;
  id?: string;
  validPeriod?: [string | null, string | null];
}

export const ClassifierGroupSelect: FC<ClassifierGroupSelectProps> = (
  props
) => {
  const getIdFromName = useGetIdFromName();
  const { name, id = getIdFromName(name), validPeriod, ...rest } = props;
  const { dataIndex } = extractRowIndexAndColumnName(name);
  const baseName = `dimensions.${dataIndex}` as const;
  const { control, setValue } = useFormContext<DataObjectValidFormDataType>();
  const { data, isLoading } = useClassifierGroupsQuery();

  const period = useMemo(
    () =>
      validPeriod
        ? getDatePeriodArray(validPeriod[0], validPeriod[1])
        : undefined,
    [validPeriod]
  );

  const getchildren = useCallback(
    (classifier: ClassifierGroupType) => {
      const children: ClassifierOptionType[] = [];

      if (classifier.hasCategories) {
        children.push({
          value: `${classifier.id}-all-categories`,
          title: getCategoryLabel(true),
          displayLabel: (
            <DimensionClassifierValue classifier={classifier} allCategories />
          ),
          classifier,
          allCategories: true,
        });
      }

      const categories = filterCategoryPeriod(classifier, period);
      if (categories) {
        children.push(
          ...categories.map((category) => ({
            value: `${classifier.id}-${category.id}`,
            title: formatCodeName(category),
            displayLabel: (
              <DimensionClassifierValue
                classifier={classifier}
                category={category}
              />
            ),
            classifier,
            category,
          }))
        );
      }

      if (
        Array.isArray(classifier.categoryHierarchyLevelNumbers) &&
        classifier.categoryHierarchyLevelNumbers.length > 1
      ) {
        children.push(
          ...classifier.categoryHierarchyLevelNumbers.map((levelNumber) => ({
            value: `${classifier.id}-${levelNumber}`,
            title: getCategoryLabel(undefined, levelNumber),
            displayLabel: (
              <DimensionClassifierValue
                classifier={classifier}
                levelNumber={levelNumber}
              />
            ),
            classifier,
            levelNumber,
          }))
        );
      }

      return children;
    },
    [period]
  );

  const classifiers: ClassifierOptionType[] = useMemo(
    () =>
      filterClassifierPeriod(data, period).map((classifier) => ({
        value: classifier.id,
        title: formatCodeName(classifier),
        displayLabel: <DimensionClassifierValue classifier={classifier} />,
        classifier,
        children: getchildren(classifier),
      })) ?? [],
    [data, period, dataIndex, getchildren]
  );

  return (
    <Controller
      control={control}
      name={name as `dimensions.${number}.classifier`}
      render={({ field }) => {
        const { disabled } = field;
        const classifierId = field.value?.classifier.id;

        const value = isLoading
          ? null
          : field.value?.allCategories
          ? `${classifierId}-all-categories`
          : field.value?.category
          ? `${classifierId}-${field.value?.category.id}`
          : field.value?.levelNumber
          ? `${classifierId}-${field.value?.levelNumber}`
          : classifierId;

        const onSelect = (
          _: string | number | null | undefined,
          option: ClassifierOptionType
        ) => {
          const {
            classifier,
            allCategories = null,
            category = null,
            levelNumber = null,
          } = option;
          const opts = { shouldDirty: true };

          if (classifier.id !== classifierId) {
            setValue(`${baseName}.constantValue`, null, opts);
          }
          setValue(`${baseName}.code`, classifier.code, opts);
          setValue(`${baseName}.name`, classifier.name, opts);

          const aggregateHierarchyDisabled =
            category != null ||
            levelNumber != null ||
            (classifier.categoryHierarchyLevelNumbers || []).length <= 1;
          setValue(
            `${baseName}.aggregateHierarchy`,
            aggregateHierarchyDisabled ? null : false
          );

          field.onChange({ classifier, allCategories, category, levelNumber });
        };

        return (
          <TreeSelect<string | number | null | undefined, ClassifierOptionType>
            {...rest}
            id={id}
            allowClear
            disabled={disabled}
            styles={{
              popup: {
                root: {
                  marginRight: '5px',
                  maxHeight: 400,
                  overflow: 'auto',
                  width: 400,
                },
              },
            }}
            style={{ height: 'auto', lineHeight: 12 }}
            onSelect={onSelect}
            showSearch
            treeDefaultExpandAll
            treeNodeFilterProp="title"
            treeNodeLabelProp="displayLabel"
            treeData={classifiers}
            value={value}
            popupRender={(defaultNode) =>
              isLoading ? (
                <Flex justify="center">
                  <Spin size="small" />
                </Flex>
              ) : (
                defaultNode
              )
            }
          />
        );
      }}
    />
  );
};
